import moment from "moment-timezone";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  type ActiveQueueStats,
  type AgtechProxySyncFlag,
  useActivePgJobQueueStats,
  useAgtechProxySyncFlags,
  useStuckOtcContractDispatchCount,
} from "@/lib/api/developers/systemHealth";
import { CommonDestinationTimezone } from "@/lib/utils";
import { cn } from "@/lib/utils";

function SectionHeader({
  title,
  isFetching,
  onRefresh,
}: {
  title: string;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isFetching}
        onClick={onRefresh}
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        {isFetching ? "Refreshing" : "Refresh"}
      </Button>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "None";
  return moment.tz(value, CommonDestinationTimezone).format("YYYY/MM/DD h:mm A z");
}

function OtcContractDispatchHealth() {
  const result = useStuckOtcContractDispatchCount();
  const count = result.data?.data?.count;

  return (
    <section className="space-y-2">
      <SectionHeader
        title="OTC Contract Dispatch"
        isFetching={result.isFetching}
        onRefresh={() => { void result.refetch(); }}
      />
      {result.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading OTC stuck contract statuses</p>
      ) : result.isError || !result.data?.success || count === undefined ? (
        <p className="text-sm text-red-600">Unable to load OTC stuck contract statuses</p>
      ) : count === 0 ? (
        <p className="text-sm">
          OTC Contract Dispatch, <span className="font-medium text-green-600">System nominal</span>
        </p>
      ) : (
        <p className="text-sm text-red-600">
          OTC Contract Dispatch has <span className="font-semibold">{count}</span> stuck {count === 1 ? "entry" : "entries"}.
        </p>
      )}
    </section>
  );
}

function FlagStatus({ flag }: { flag: AgtechProxySyncFlag }) {
  return (
    <Card className="shadow-none">
      <CardContent className="space-y-1 p-3">
        <p className="break-words text-sm font-medium">{flag.flag_type}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <span>Enabled: <strong className={flag.enabled ? "text-green-600" : "text-red-600"}>{flag.enabled ? "True" : "False"}</strong></span>
          <span>Status: <strong className={flag.flag_status ? "text-red-600" : "text-green-600"}>{flag.flag_status ? "True" : "False"}</strong></span>
        </div>
        <p className="text-xs text-muted-foreground">Last updated: {formatDate(flag.last_updated)}</p>
      </CardContent>
    </Card>
  );
}

function AgtechSyncHealth() {
  const result = useAgtechProxySyncFlags();
  const flags = result.data?.data ?? [];
  const isNominal = flags.length === 2
    && flags.every((flag: AgtechProxySyncFlag): boolean => flag.enabled && !flag.flag_status);

  return (
    <section className="space-y-2">
      <SectionHeader
        title="Agtech Sync"
        isFetching={result.isFetching}
        onRefresh={() => { void result.refetch(); }}
      />
      {result.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading Agtech sync statuses</p>
      ) : result.isError || !result.data?.success ? (
        <p className="text-sm text-red-600">Unable to load Agtech sync statuses</p>
      ) : isNominal ? (
        <p className="text-sm font-medium text-green-600">Agtech Sync, Systems Nominal</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {flags.map((flag: AgtechProxySyncFlag) => (
            <FlagStatus key={flag.flag_type} flag={flag} />
          ))}
          {flags.length < 2 && (
            <p className="text-sm text-red-600">One or more Agtech sync status flags are missing.</p>
          )}
        </div>
      )}
    </section>
  );
}

function QueueStatsCard({ queue }: { queue: ActiveQueueStats }) {
  return (
    <Card className="shadow-none">
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="break-words text-sm font-semibold">{queue.name}</p>
          <span className="shrink-0 text-xs text-muted-foreground">Queue {queue.queue_id}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-4">
          <span className={cn(queue.pending > 0 ? "text-orange-600" : "text-muted-foreground")}>Pending: {queue.pending}</span>
          <span className={cn(queue.processing > 0 ? "text-green-600" : "text-muted-foreground")}>Processing: {queue.processing}</span>
          <span className={cn(queue.failed > 0 ? "text-red-600" : "text-muted-foreground")}>Failed: {queue.failed}</span>
          <span className={cn((queue.halted > 0 && moment(queue.latest_halted_at).isAfter(moment().subtract(24, "hours"))) ? "text-red-600" : "text-muted-foreground")}>Halted: {queue.halted}</span>
        </div>
        <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
          <span>Earliest halted: {formatDate(queue.earliest_halted_at)}</span>
          <span>Latest halted: {formatDate(queue.latest_halted_at)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function PgJobQueueHealth() {
  const result = useActivePgJobQueueStats();
  const queues = result.data?.data ?? [];

  return (
    <section className="space-y-2">
      <SectionHeader
        title="PG Job Queues"
        isFetching={result.isFetching}
        onRefresh={() => { void result.refetch(); }}
      />
      {result.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading PG job queue statuses</p>
      ) : result.isError || !result.data?.success ? (
        <p className="text-sm text-red-600">Unable to load PG job queue statuses</p>
      ) : queues.length === 0 ? (
        <p className="text-sm text-muted-foreground">No PG job queues found</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {queues.map((queue: ActiveQueueStats) => (
            <QueueStatsCard key={queue.queue_id} queue={queue} />
          ))}
        </div>
      )}
    </section>
  );
}

export function SystemHealthPage() {
  return (
    <div className="flex flex-col gap-5 pb-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">System Health</h1>
        <p className="text-sm text-muted-foreground">Developer-only operational status checks.</p>
      </div>
      <OtcContractDispatchHealth />
      <AgtechSyncHealth />
      <PgJobQueueHealth />
    </div>
  );
}
